import { useMemo } from 'react';

import { Text } from '@chakra-ui/react';

import { dateHelpers } from '@nocowanie/core';
import { usePluralisationTranslation, wordPluralisation } from '@nocowanie/common-ui';

import { getDates } from '@app/helpers/accommodation.helpers';

import { GuestsNightsLabelProps } from './guests-nights-label.props';

export const GuestsNightsLabel = ({
    adultsCount,
    childrenCount,
    nightsCount,
    checkIn,
    checkOut,
    t,
    withListingTile = false,
    ...props
}: GuestsNightsLabelProps) => {
    const pluralizeTranslation = usePluralisationTranslation('accommodation', 'guestsNightsLabel');

    const dates = getDates(checkIn ?? new Date(), checkOut ?? new Date());
    const nights = useMemo(
        () =>
            checkIn && checkOut
                ? Math.max(1, dateHelpers.daysDateDiff(dates[0], dates[1]))
                : (nightsCount as number),
        [checkIn, checkOut, dates, nightsCount],
    );

    const translatedNights = t?.('guestsNightsLabel.night', { returnObjects: true }) as Record<
        string,
        string
    >;
    const translatedGuests = t?.('guestsNightsLabel.person', { returnObjects: true }) as Record<
        string,
        string
    >;
    const totalGuests = `${adultsCount}${childrenCount ? ` + ${childrenCount}` : ''}`;

    const nightsLabel = t
        ? `${nights} ${wordPluralisation(
              nights,
              translatedNights.singular,
              translatedNights.plural,
              translatedNights.genitivePlural,
          )}`
        : pluralizeTranslation(nights, 'night');
    const valueToPluralize = childrenCount > 0 ? childrenCount : adultsCount;
    const totalGuestsLabel = t
        ? `${totalGuests} ${wordPluralisation(
              valueToPluralize,
              translatedGuests.singular,
              translatedGuests.plural,
              translatedGuests.genitivePlural,
          )}`
        : pluralizeTranslation(valueToPluralize, 'person', totalGuests);

    return (
        <Text as={'span'} whiteSpace={'nowrap'} fontSize={'xs'} {...props}>
            {withListingTile
                ? `za ${nightsLabel}, ${totalGuestsLabel}`
                : `${totalGuestsLabel}, ${nightsLabel}`}
        </Text>
    );
};
